import * as ol from 'openlayers'
import { LayerDescription, LayerParamsObject, LayerType } from './LayerRegistry'

/**
 * Interface for the base AbstractLayer class.
 */
export interface GeoLayer {
  olLayer: ol.layer.Layer
  enabled: boolean
  visible: boolean
  type: LayerType
  zIndex: number
  getSubLayerName: (params: LayerParamsObject, value: number) => string
  onZoomChange: (zoomLevel: number) => void
  getMinimumZoom: () => number
  describe: () => LayerDescription
}

/**
 * Interface for the options object given to the base AbstractLayer
 * class' constructor.
 */
export interface AbstractLayerOptions {
  layerType: LayerType
  minZoom?: number
  enabled?: boolean
  zIndex: number
}

/**
 * Interface for properties of a sub layer.
 */
export interface SubLayers {
  labelId: string
  key: string
  list: string[]
  subLayerDescriptors: SubLayerDescriptor[]
  inputEnabled?: boolean
  stepperEnabled?: boolean
  unit?: string
  minimum: number
  maximum: number
  step: number
  selected: SubLayerDescriptor
}

export interface SubLayerDescriptor {
  value: number
  subLayer: string
  label?: string
  subLabel?: string
  wikiKey?: string
}

/**
 * Interface for properties of a radio button sub layer group.
 */
export interface RadioSubLayers {
  labelId: string
  selected: RadioSubLayer
  sublayers: RadioSubLayer[]
}

/**
 * Interface for properties of a radio button sub layer.
 */
export interface RadioSubLayer {
  labelId: string
  subLayer: string
  path?: string
}

/**
 * Interface for properties of a switch sub layer group.
 */
export interface SwitchSubLayers {
  labelId: string
  sublayers: SwitchSubLayer[]
}

/**
 * Interface for properties of a switch sub layer.
 */
export interface SwitchSubLayer {
  id?: string
  labelId?: string
  title?: string
  subLayer: string
  enabled: boolean
}

/**
 * Abstract super class for all OpenLayers layers.
 */
export class AbstractLayer implements GeoLayer {
  _minimumZoom: number
  _enabled: boolean
  _olLayer: ol.layer.Layer
  _type: LayerType
  _zIndex: number

  /**
   * Constructor.
   * @param {object} options The options to apply to the layer.
   * The options object has the following keys:
   * * minZoom: The minimum zoom level at which to show the layer.
   * * enabled: Whether the layer should be enabled. This does not
   *   mean visible, rather whether the layer should react to other
   *   changes from the outside, like e.g. when the zoom level is
   *   changed.
   */
  constructor (options: AbstractLayerOptions) {
    this._type = options.layerType
    this._minimumZoom = options.minZoom !== undefined ? options.minZoom : -1
    this._enabled = options.enabled !== undefined ? options.enabled : false
    this._olLayer = new ol.layer.Layer({})
    this._zIndex = options.zIndex
  }

  /**
   * Getter for the layer type
   */
  get type (): LayerType {
    return this._type
  }

  /**
   * Getter for the underlying OpenLayers layer.
   */
  get olLayer (): ol.layer.Layer {
    return this._olLayer
  }

  /**
   * Setter for the underlying OpenLayers layer.
   * All subclasses must use this setter within their constructor
   * to set the layer.
   */
  set olLayer (value) {
    this._olLayer = value
    this._olLayer.setZIndex(this._zIndex)
    this._olLayer.setProperties({ layerType: this._type })
    this._olLayer.set('geo_type', this._type, true)
  }

  /**
   * Getter for the layer zIndex.
   */
  get zIndex (): number {
    return this._zIndex
  }

  /**
   * Setter for the layer zIndex.
   */
  set zIndex (zIndex: number) {
    this._zIndex = zIndex
  }

  /**
   * Getter for the enabled property.
   */
  get enabled (): boolean {
    return this._enabled
  }

  /**
   * Setter for the enabled property.
   */
  set enabled (value) {
    this._enabled = value
  }

  /**
   * Getter for the visible property.
   * This calls down to the underlying OpenLayers layer, so calling
   * it before initializing that will result in an error.
   */
  get visible (): boolean {
    if (this._olLayer === undefined) {
      throw new Error('OpenLayers layer has not been initialized.')
    }
    return this._olLayer.getVisible()
  }

  /**
   * Setter for the visible property.
   * This calls down to the underlying OpenLayers layer, so calling
   * it before initializing that will result in an error.
   */
  set visible (value) {
    if (this._olLayer === undefined) {
      throw new Error('OpenLayers layer has not been initialized.')
    }
    this._olLayer.setVisible(value)
  }

  /**
   * Get sub layer name of a given value.
   * @param params Params to get sub layer name from.
   * @param value Sub layer value.
   * @returns Name of sub layer.
   */
  getSubLayerName (params: LayerParamsObject, value: number): string {
    return ''
  }

  /**
   * Getter for minimum zoom level.
   * @returns The minimum zoom level to enable the layer on.
   */
  getMinimumZoom (): number {
    return this._minimumZoom
  }

  /**
   * Method that can be called when the Map component changes
   * zoom level. The layer will toggle its visible property if
   * needed.
   * @param {number} zoomLevel The current zoom level.
   */
  onZoomChange (zoomLevel: number): void {
    if (this.enabled) {
      if (this._minimumZoom !== -1) {
        if (zoomLevel < this._minimumZoom && this.visible) {
          this.visible = false
        } else if (zoomLevel >= this._minimumZoom && !this.visible) {
          this.visible = true
        }
      }
    }
  }

  describe (): LayerDescription {
    throw new Error('Mandatory method describe is missing in subclass.')
  }
}
